/**
 * ChatContainer - Minimalist mission control styled chat interface
 *
 * Features:
 * - Clean 60/40 split layout
 * - Retro mission control aesthetic
 * - WebSocket status indicator
 * - Streamlined header
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useWebSocket } from '../../hooks/useWebSocket'
import { useChatHistory, type ChatMessageWithReaction } from '../../hooks/useChatHistory'
import { ConnectionStatus, Artifact } from '../../types/chat'
import { createReactionHandler } from '../../lib/feedback'
import MessageList from './MessageList'
import MessageInput from './MessageInput'
import ArtifactPanel from './ArtifactPanel'
import SandboxToggle from './SandboxToggle'
import ReactionBar from '../Feedback/ReactionBar'
import { ConnectionSkeleton } from '../LoadingSkeleton'
import { RefreshCw, Menu } from 'lucide-react'
import { supabase } from '../../lib/supabase'

interface ChatContainerProps {
  sessionId: string
  /** Opens the session drawer — only rendered on mobile (<md). */
  onMenuClick?: () => void
}

function ChatContainer({ sessionId, onMenuClick }: ChatContainerProps) {
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [sandboxEnabled, setSandboxEnabled] = useState(false)
  const [referenceTime, setReferenceTime] = useState<string | null>(null)

  const handleSandboxToggle = useCallback((enabled: boolean, refTime: string | null) => {
    setSandboxEnabled(enabled)
    setReferenceTime(refTime)
  }, [])

  // Get auth token from Supabase session
  useEffect(() => {
    const getToken = async () => {
      if (!supabase) {
        // Supabase not configured — dev mode, proceed without token
        return
      }
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.access_token) {
        setAuthToken(session.access_token)
      }
    }
    getToken()

    // Listen for auth changes
    if (!supabase) return
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthToken(session?.access_token || null)
    })

    return () => subscription.unsubscribe()
  }, [])

  // Load chat history + reactions for existing sessions
  const {
    messages: historyMessages,
    sessionReaction,
    loading: historyLoading,
    createDispatch,
  } = useChatHistory(sessionId)

  const { messages: liveMessages, connectionStatus, reconnectAttempts, sendMessage, reconnect } = useWebSocket({
    sessionId,
    token: authToken,
  })

  // Merge history + live. History seeds the view on first load; live messages append.
  // If the same message id is present in both, the live version wins (may have newer
  // streaming content), but we carry over the stored reaction so the thumb survives.
  const messages: ChatMessageWithReaction[] = useMemo(() => {
    const liveWithIds = new Set(liveMessages.filter((m) => m.id).map((m) => m.id as string))
    const historyOnly = historyMessages.filter((m) => !m.id || !liveWithIds.has(m.id))
    const liveWithReactions: ChatMessageWithReaction[] = liveMessages.map((m) => {
      const fromHistory = m.id ? historyMessages.find((h) => h.id === m.id) : undefined
      return { ...m, reaction: fromHistory?.reaction ?? null }
    })
    return [...historyOnly, ...liveWithReactions]
  }, [historyMessages, liveMessages])

  // Note: sendMessage is the sole gate for "is the socket open?" — it reads
  // wsRef.current.readyState directly, which is more current than the
  // connectionStatus React state (which can lag a render). Pre-gating here
  // caused the silent PLAN MANEUVER drop in run-006: UI showed ONLINE but
  // the handler's closure had a stale connectionStatus.
  const handleSendMessage = useCallback(
    (content: string) => {
      const trimmed = content.trim()
      if (!trimmed) return
      sendMessage(trimmed, sandboxEnabled ? referenceTime : null)
    },
    [sendMessage, sandboxEnabled, referenceTime],
  )

  const handlePlanManeuver = useCallback(
    (cdmId: string) => {
      const message = `Plan a collision avoidance maneuver for CDM ${cdmId}`
      sendMessage(message, sandboxEnabled ? referenceTime : null)
    },
    [sendMessage, sandboxEnabled, referenceTime],
  )

  const isConnected = connectionStatus === ConnectionStatus.CONNECTED

  // Collect all artifacts from messages
  const allArtifacts: Artifact[] = messages
    .filter((msg) => msg.role === 'agent' && msg.artifacts && msg.artifacts.length > 0)
    .flatMap((msg) => msg.artifacts || [])

  // Connection status config
  const statusConfig = {
    [ConnectionStatus.CONNECTED]: { color: '#66cccc', text: 'ONLINE' },
    [ConnectionStatus.CONNECTING]: { color: '#ffcc66', text: 'CONNECTING' },
    [ConnectionStatus.RECONNECTING]: { color: '#ffcc66', text: 'RECONNECTING' },
    [ConnectionStatus.DISCONNECTED]: { color: '#666666', text: 'OFFLINE' },
    [ConnectionStatus.ERROR]: { color: '#ff6666', text: 'ERROR' },
  }

  const status = statusConfig[connectionStatus] || statusConfig[ConnectionStatus.DISCONNECTED]

  const showReconnectBanner =
    connectionStatus === ConnectionStatus.RECONNECTING ||
    connectionStatus === ConnectionStatus.DISCONNECTED ||
    connectionStatus === ConnectionStatus.ERROR

  const isConnecting = historyLoading

  return (
    <div className="h-full bg-retro-bg text-dark-600 font-mono" data-testid="chat-container">
      {/* Header - Compact */}
      <header className="border-b border-retro-border px-4 py-2">
        <div className="flex items-center justify-between gap-2">
          {/* Logo and title */}
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            {/* Hamburger — opens the session drawer on mobile. */}
            <button
              onClick={onMenuClick}
              className="p-1 -ml-1 rounded hover:bg-retro-surface transition-colors text-retro-dim md:hidden"
              title="Sessions"
              aria-label="Open sessions"
            >
              <Menu className="w-5 h-5" />
            </button>
            <img src="/favicon.svg" alt="" className="w-5 h-5 flex-shrink-0" />
            <h1
              className="text-sm font-bold tracking-widest uppercase truncate"
              style={{ color: '#ffcc66' }}
            >
              Space Operator Copilot
            </h1>
          </div>

          {/* Status indicators */}
          <div className="flex items-center gap-2 md:gap-4 text-xs flex-shrink-0">
            {/* Connection */}
            <div className="flex items-center gap-2">
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: status.color }}
              />
              <span className="hidden sm:inline text-retro-dim uppercase tracking-wider">{status.text}</span>
            </div>

            {/* Session-level reaction — "this whole conversation" feedback. */}
            <ReactionBar
              variant="session"
              testId="reaction-bar-session"
              reaction={sessionReaction}
              handler={createReactionHandler(
                { sessionId, messageId: null },
                createDispatch(null),
              )}
            />

            <SandboxToggle
              enabled={sandboxEnabled}
              referenceTime={referenceTime}
              onToggle={handleSandboxToggle}
            />

            {/* USSPACECOM data attribution (issue #577). */}
            {/* Keep in sync: README §Data Sources, CLI ingest footer. */}
            <span
              className="hidden lg:inline text-retro-subtle uppercase tracking-wider"
              title="Orbital data provided by the 18th Space Defense Squadron (18 SDS), United States Space Force, via Space-Track.org."
              data-testid="attribution-chip"
            >
              Data:{' '}
              <a
                href="https://www.space-track.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-retro-amber hover:text-retro-orange"
              >
                18 SDS / Space-Track
              </a>
            </span>
          </div>
        </div>
      </header>

      {/* Reconnection Banner - Compact */}
      {showReconnectBanner && (
        <div className="border-b border-retro-border px-4 py-2 bg-retro-surface">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {connectionStatus === ConnectionStatus.RECONNECTING ? (
                <RefreshCw className="w-4 h-4 animate-spin" style={{ color: '#ffcc66' }} />
              ) : (
                <div className="w-4 h-4 flex items-center justify-center">
                  <span style={{ color: '#ff6666' }}>!</span>
                </div>
              )}
              <span className="text-xs" style={{ color: '#ffcc66' }}>
                {connectionStatus === ConnectionStatus.RECONNECTING
                  ? `Reconnecting... (${reconnectAttempts})`
                  : 'Connection lost'}
              </span>
            </div>
            {connectionStatus !== ConnectionStatus.RECONNECTING && (
              <button
                onClick={reconnect}
                className="text-xs px-3 py-1 rounded border border-retro-amber text-retro-amber hover:bg-retro-amber/10 transition-colors"
              >
                Reconnect
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Content — fills the shell content area below the 41px header
          (the app-level tab bar lives in AppShell, outside this component). */}
      <main className="flex h-[calc(100%-41px)]">
        {/* Chat Panel (60% on desktop, full width on mobile — the artifact
            panel becomes an overlay below md). */}
        <div className="flex-1 md:flex-[3] md:border-r border-retro-border flex flex-col min-w-0">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            <div className="max-w-3xl mx-auto">
              {isConnecting ? (
                <ConnectionSkeleton />
              ) : (
                <MessageList
                  messages={messages}
                  onSendMessage={handleSendMessage}
                  onPlanManeuver={handlePlanManeuver}
                  createReactionDispatch={createDispatch}
                  disabled={!isConnected}
                />
              )}
            </div>
          </div>

          {/* Input - Clean bottom bar */}
          <div className="border-t border-retro-border px-4 md:px-6 py-3 bg-retro-surface">
            <div className="max-w-3xl mx-auto">
              <MessageInput
                onSendMessage={handleSendMessage}
                disabled={!isConnected}
                disabledPlaceholder={
                  connectionStatus === ConnectionStatus.RECONNECTING
                    ? `Reconnecting... (${reconnectAttempts})`
                    : 'Connecting...'
                }
              />
            </div>
          </div>
        </div>

        {/* Artifact Panel */}
        <ArtifactPanel
          artifacts={allArtifacts}
          onPlanManeuver={handlePlanManeuver}
          disabled={!isConnected}
        />
      </main>
    </div>
  )
}

export default ChatContainer
